import { useState, useEffect, useCallback, useRef } from 'react';
import {
  listCharacters,
  createCharacter,
  updateCharacter,
  deleteCharacter,
  type ContentCharacter,
} from '@/lib/contentStudio';
import { uploadMediaFile } from '@/hooks/useUpload';

interface Props {
  projectId: number;
}

const VOICE_OPTIONS = [
  'Voix homme grave', 'Voix homme chaleureuse', 'Voix femme douce',
  'Voix femme énergique', 'Voix narrateur documentaire', 'Voix enfantine', 'Aucune',
];

interface DraftState {
  name: string;
  description: string;
  appearance: string;
  reference_image: string;
  voice: string;
}

const EMPTY_DRAFT: DraftState = { name: '', description: '', appearance: '', reference_image: '', voice: '' };

/**
 * Personnages persistants du projet : chaque personnage (apparence, tenue,
 * image de référence, voix) est réutilisé dans toutes les scènes pour garantir
 * une cohérence visuelle.
 */
export default function CharactersPanel({ projectId }: Props) {
  const [characters, setCharacters] = useState<ContentCharacter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<ContentCharacter | null>(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setCharacters(await listCharacters(projectId));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setCreating(true);
    setEditing(null);
    setDraft(EMPTY_DRAFT);
  };

  const openEdit = (c: ContentCharacter) => {
    setEditing(c);
    setCreating(false);
    setDraft({
      name: c.name,
      description: c.description || '',
      appearance: c.appearance || '',
      reference_image: c.reference_image || '',
      voice: c.voice || '',
    });
  };

  const closeForm = () => {
    setCreating(false);
    setEditing(null);
    setDraft(EMPTY_DRAFT);
  };

  const handleUpload = async (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError("L'image de référence doit être un fichier image (JPG, PNG, WEBP).");
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const url = await uploadMediaFile(file, 'content-studio/characters');
      setDraft((d) => ({ ...d, reference_image: url }));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Échec de l'envoi de l'image");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleSave = async () => {
    if (!draft.name.trim() || saving) return;
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: draft.name.trim().slice(0, 100),
        description: draft.description || null,
        appearance: draft.appearance || null,
        reference_image: draft.reference_image || null,
        voice: draft.voice || null,
      };
      if (editing) {
        await updateCharacter(editing.id, payload);
      } else {
        await createCharacter(projectId, payload);
      }
      closeForm();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur d\u2019enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (c: ContentCharacter) => {
    try {
      await deleteCharacter(c.id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur de suppression');
    }
  };

  const formOpen = creating || !!editing;

  return (
    <div>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="text-base font-bold font-heading text-foreground-950 flex items-center gap-2">
            <i className="ri-group-line text-foreground-500"></i>Personnages
          </h3>
          <p className="text-xs text-foreground-500 mt-1 max-w-2xl">
            Créez des personnages persistants (apparence, tenue, voix). Ils seront réutilisés dans toutes les scènes
            pour garder un rendu cohérent d'une scène à l'autre.
          </p>
        </div>
        {!formOpen && (
          <button
            type="button"
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-user-add-line"></i>Ajouter un personnage
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
          <i className="ri-error-warning-line"></i>{error}
        </div>
      )}

      {formOpen && (
        <div className="mb-5 bg-background-50 border border-accent-200/70 rounded-xl p-4">
          <h4 className="text-sm font-bold text-foreground-900 mb-3">
            {editing ? 'Modifier le personnage' : 'Nouveau personnage'}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-1">
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Image de référence</label>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="w-full aspect-square rounded-lg border border-dashed border-background-300/70 bg-background-100 hover:border-accent-300 flex flex-col items-center justify-center overflow-hidden transition-colors cursor-pointer"
              >
                {draft.reference_image ? (
                  <img src={draft.reference_image} alt={draft.name} className="w-full h-full object-cover" />
                ) : uploading ? (
                  <><i className="ri-loader-4-line animate-spin text-xl text-accent-500"></i></>
                ) : (
                  <>
                    <i className="ri-image-add-line text-2xl text-foreground-400 mb-1"></i>
                    <span className="text-[11px] text-foreground-500">Choisir une image</span>
                  </>
                )}
              </button>
              {draft.reference_image && (
                <button
                  type="button"
                  onClick={() => setDraft((d) => ({ ...d, reference_image: '' }))}
                  className="mt-1.5 text-[11px] text-foreground-500 hover:text-red-500 cursor-pointer"
                >
                  Retirer l&apos;image
                </button>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleUpload(e.target.files?.[0])}
              />
            </div>

            <div className="md:col-span-2 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1.5">Nom</label>
                  <input
                    value={draft.name}
                    onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                    placeholder="Ex : Mobutu"
                    className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-accent-300"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1.5">Voix</label>
                  <div className="relative">
                    <select
                      value={draft.voice}
                      onChange={(e) => setDraft((d) => ({ ...d, voice: e.target.value }))}
                      className="w-full appearance-none px-3 py-2 pr-8 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-accent-300 cursor-pointer"
                    >
                      <option value="">—</option>
                      {VOICE_OPTIONS.map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none"></i>
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1.5">Description</label>
                <textarea
                  value={draft.description}
                  onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                  rows={2}
                  placeholder="Rôle, personnalité, époque…"
                  className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-800 focus:outline-none focus:border-accent-300 resize-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1.5">Apparence physique</label>
                <textarea
                  value={draft.appearance}
                  onChange={(e) => setDraft((d) => ({ ...d, appearance: e.target.value }))}
                  rows={2}
                  placeholder="Visage, coiffure, tenue, silhouette…"
                  className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-800 focus:outline-none focus:border-accent-300 resize-none"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 mt-4">
            <button
              type="button"
              onClick={closeForm}
              className="px-4 py-2 rounded-full border border-background-200/70 text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!draft.name.trim() || saving}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer"
            >
              {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
              Enregistrer
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <i className="ri-loader-4-line animate-spin text-xl text-accent-500"></i>
        </div>
      ) : characters.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 bg-background-50 border border-background-200/70 rounded-xl text-center">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
            <i className="ri-group-line text-xl text-foreground-400"></i>
          </div>
          <p className="text-sm font-medium text-foreground-700">Aucun personnage</p>
          <p className="text-xs text-foreground-500 mt-1 max-w-xs">
            Ajoutez vos personnages pour garder le même look dans toutes les scènes.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {characters.map((c) => (
            <div key={c.id} className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden flex flex-col">
              <div className="h-32 bg-gradient-to-br from-background-100 to-background-200/60 flex items-center justify-center overflow-hidden">
                {c.reference_image ? (
                  <img src={c.reference_image} alt={c.name} className="w-full h-full object-cover" />
                ) : (
                  <i className="ri-user-3-line text-3xl text-foreground-300"></i>
                )}
              </div>
              <div className="p-3 flex-1 flex flex-col">
                <h4 className="text-sm font-bold text-foreground-900 truncate">{c.name}</h4>
                {c.voice && (
                  <span className="mt-1 inline-flex items-center gap-1 self-start px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-800 text-[10px] font-medium">
                    <i className="ri-mic-line"></i>{c.voice}
                  </span>
                )}
                {c.description && <p className="text-xs text-foreground-600 mt-2 line-clamp-2">{c.description}</p>}
                {c.appearance && (
                  <p className="text-[11px] text-foreground-400 mt-1 line-clamp-2 flex items-start gap-1">
                    <i className="ri-eye-line mt-0.5"></i>{c.appearance}
                  </p>
                )}
                <div className="flex items-center gap-2 mt-3 pt-2 border-t border-background-200/70">
                  <button
                    type="button"
                    onClick={() => openEdit(c)}
                    className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-full border border-background-200/70 text-xs font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <i className="ri-edit-line"></i>Modifier
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(c)}
                    className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                    title="Supprimer"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}